import React, { useState } from "react";
import { ChevronLeft, ChevronRight, Play, Square } from "lucide-react";
import { CircularBpmKnob } from "../ui/CircularBpmKnob";
import { TaskCreatorButton } from "../TaskCreatorButton";
import { serializeScaleState } from "../../lib/taskEngine";
import type { NoteName } from "../../lib/guitarEngine";
import { SATURATED_DEGREE_HEX } from "../../lib/scaleModuleTheme";

const STEP_META = [
  { level: 1 as const, short: "I", label: "Tónica", color: SATURATED_DEGREE_HEX[0] },
  { level: 2 as const, short: "III", label: "Triada", color: "#5BA8A8" },
  { level: 3 as const, short: "V", label: "Penta", color: "#D4A373" },
  { level: 4 as const, short: "8", label: "8va", color: "#8FB069" },
];

const ROUTES = [
  { mode: "off" as const, label: "✕", title: "Sin ruta" },
  { mode: "tonic" as const, label: "🎯", title: "Tónica" },
  { mode: "transition" as const, label: "🔗", title: "Transición" },
  { mode: "recorrido" as const, label: "🐍", title: "Recorrido" },
];

function DockDivider() {
  return <div className="w-px self-stretch bg-white/10 shrink-0 mx-0.5 min-h-[28px]" aria-hidden />;
}

function DockGroup({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col items-start gap-1 shrink-0" title={hint}>
      <span className="text-[9px] font-sans font-semibold uppercase tracking-wider text-slate-500 px-0.5 leading-none">
        {label}
      </span>
      <div className="flex items-center gap-0.5">{children}</div>
    </div>
  );
}

function IconChip({
  active,
  disabled,
  onClick,
  title,
  children,
  className = "",
}: {
  active?: boolean;
  disabled?: boolean;
  onClick?: () => void;
  title: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <button
      type="button"
      title={title}
      disabled={disabled}
      onClick={onClick}
      className={`h-8 min-w-[2rem] px-1.5 rounded-lg text-xs font-sans transition-all shrink-0 ${
        disabled ? "opacity-35 cursor-not-allowed" : ""
      } ${
        active
          ? "bg-[var(--bordona-accent)] text-white shadow-sm"
          : "bg-white/[0.07] text-slate-300 hover:bg-white/12"
      } ${className}`}
    >
      {children}
    </button>
  );
}

export interface ScaleHorizontalDockProps {
  mode: "learn" | "practice";
  rootNote: NoteName;
  scaleLabel: string;
  scaleType: string;
  notes: readonly NoteName[];
  scaleOptions: readonly { value: string; label: string }[];
  onPrevNote: () => void;
  onNextNote: () => void;
  onPrevScale: () => void;
  onNextScale: () => void;
  onSelectNote: (n: NoteName) => void;
  onSelectScale: (value: string) => void;
  pedagogicalLevel: 1 | 2 | 3;
  onPedagogicalLevel: (l: 1 | 2 | 3) => void;
  focusLevel: 1 | 2 | 3 | 4 | 5;
  onFocusLevel: (l: 1 | 2 | 3 | 4 | 5) => void;
  /** Pasos del módulo (piano nivel 1 = 5 pasos). */
  focusSteps?: ReadonlyArray<{
    level: 1 | 2 | 3 | 4 | 5;
    short: string;
    label: string;
    color: string;
  }>;
  routeMode: "off" | "tonic" | "transition" | "recorrido";
  onRouteMode: (m: "off" | "tonic" | "transition" | "recorrido") => void;
  routeLocked?: boolean;
  octaves: number;
  onOctaves: (n: number) => void;
  direction: "asc" | "desc" | "both";
  onDirection: (d: "asc" | "desc" | "both") => void;
  pattern: string;
  bpm: number;
  onBpm: (n: number) => void;
  /** Aprender */
  learnCtaLabel: string;
  onLearnPrimary: () => void;
  learnBusy?: boolean;
  /** Practicar */
  isPlaying?: boolean;
  onStartPractice?: () => void;
  onStopPractice?: () => void;
  practiceElapsed?: number;
  practiceScore?: number;
  practiceAttempts?: number;
  currentSeqIndex?: number;
  sequenceLength?: number;
  isListening?: boolean;
  /** Sandbox Explorar: sin niveles ni misiones en el dock */
  sandbox?: boolean;
}

export const ScaleHorizontalDock: React.FC<ScaleHorizontalDockProps> = (props) => {
  const {
    mode,
    rootNote,
    scaleLabel,
    scaleType,
    notes,
    scaleOptions,
    onPrevNote,
    onNextNote,
    onPrevScale,
    onNextScale,
    onSelectNote,
    onSelectScale,
    pedagogicalLevel,
    onPedagogicalLevel,
    focusLevel,
    onFocusLevel,
    focusSteps,
    routeMode,
    onRouteMode,
    routeLocked = false,
    octaves,
    onOctaves,
    direction,
    onDirection,
    pattern,
    bpm,
    onBpm,
    learnCtaLabel,
    onLearnPrimary,
    learnBusy = false,
    isPlaying = false,
    onStartPractice,
    onStopPractice,
    practiceElapsed = 0,
    practiceScore = 0,
    practiceAttempts = 0,
    currentSeqIndex = 0,
    sequenceLength = 0,
    isListening = false,
    sandbox = false,
  } = props;

  const [noteOpen, setNoteOpen] = useState(false);
  const [scaleOpen, setScaleOpen] = useState(false);

  const precision =
    practiceAttempts > 0 ? Math.round((practiceScore / practiceAttempts) * 100) : 0;
  const timeStr = `${Math.floor(practiceElapsed / 60)}:${String(practiceElapsed % 60).padStart(2, "0")}`;

  return (
    <div className="relative w-full">
      {(noteOpen || scaleOpen) && (
        <button
          type="button"
          className="fixed inset-0 z-40 cursor-default"
          aria-label="Cerrar selector"
          onClick={() => {
            setNoteOpen(false);
            setScaleOpen(false);
          }}
        />
      )}

      <div className="flex flex-wrap items-end gap-x-4 gap-y-2 px-2 py-2 min-h-[64px]">
        {!sandbox && (
        <>
        {/* Tónica + escala */}
        <DockGroup label="Escala" hint="Tónica y tipo de escala">
        <div className="flex items-center gap-0.5 shrink-0">
          <button
            type="button"
            onClick={onPrevNote}
            className="w-7 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:bg-white/10"
            title="Nota anterior"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <div className="relative">
            <button
              type="button"
              onClick={() => {
                setNoteOpen((o) => !o);
                setScaleOpen(false);
              }}
              className="w-10 h-10 rounded-full bg-gradient-to-br from-[var(--bordona-accent-hover)] to-[var(--bordona-accent)] text-white text-xl font-serif font-bold shadow-md"
              title="Cambiar tónica"
            >
              {rootNote}
            </button>
            {noteOpen && (
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 z-50 p-2 rounded-xl bg-[#1a1a1a]/95 border border-white/15 shadow-2xl backdrop-blur-md">
                <div className="grid grid-cols-6 gap-1 w-[min(16rem,70vw)]">
                  {notes.map((n) => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => {
                        onSelectNote(n);
                        setNoteOpen(false);
                      }}
                      className={`h-8 rounded-md text-xs font-bold ${
                        rootNote === n
                          ? "bg-[var(--bordona-accent)] text-white"
                          : "bg-white/10 text-slate-200 hover:bg-white/15"
                      }`}
                    >
                      {n}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={onNextNote}
            className="w-7 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:bg-white/10"
            title="Nota siguiente"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
          <div className="relative ml-1">
            <button
              type="button"
              onClick={() => {
                setScaleOpen((o) => !o);
                setNoteOpen(false);
              }}
              className="h-8 px-2 rounded-lg bg-white/[0.06] hover:bg-white/10 text-sm font-serif italic text-slate-100 max-w-[5.5rem] truncate"
              title="Cambiar tipo de escala"
            >
              {scaleLabel}
            </button>
            {scaleOpen && (
              <div className="absolute bottom-full left-0 mb-2 z-50 p-1.5 rounded-xl bg-[#1a1a1a]/95 border border-white/15 shadow-2xl backdrop-blur-md min-w-[10rem] max-h-48 overflow-y-auto">
                {scaleOptions.map((s) => (
                  <button
                    key={s.value}
                    type="button"
                    onClick={() => {
                      onSelectScale(s.value);
                      setScaleOpen(false);
                    }}
                    className={`w-full text-left px-2.5 py-1.5 rounded-lg text-xs font-sans ${
                      scaleType === s.value
                        ? "bg-[var(--bordona-accent)] text-white"
                        : "text-slate-200 hover:bg-white/10"
                    }`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={onPrevScale}
            className="w-6 h-8 rounded-lg flex items-center justify-center text-slate-500 hover:bg-white/10"
            title="Escala anterior"
          >
            <ChevronLeft className="w-3.5 h-3.5" />
          </button>
          <button
            type="button"
            onClick={onNextScale}
            className="w-6 h-8 rounded-lg flex items-center justify-center text-slate-500 hover:bg-white/10"
            title="Escala siguiente"
          >
            <ChevronRight className="w-3.5 h-3.5" />
          </button>
        </div>
        </DockGroup>

        <DockDivider />
        </>
        )}

        {!sandbox && (
        <>
        {/* Nivel */}
        <DockGroup label="Nivel" hint="Nivel pedagógico (1 = básico · 3 = avanzado)">
          {([1, 2, 3] as const).map((lvl) => (
            <IconChip
              key={lvl}
              active={pedagogicalLevel === lvl}
              onClick={() => onPedagogicalLevel(lvl)}
              title={`Nivel ${lvl}`}
              className="min-w-[1.75rem] font-bold"
            >
              {lvl}
            </IconChip>
          ))}
        </DockGroup>

        <DockDivider />

        {/* Pasos */}
        <DockGroup label="Paso" hint="Etapa actual del aprendizaje">
          {(focusSteps ?? STEP_META).map((step) => {
            const isActive = focusLevel === step.level;
            const isPast = focusLevel > step.level;
            return (
              <button
                key={step.level}
                type="button"
                title={step.label}
                onClick={() => onFocusLevel(step.level)}
                className={`h-8 w-8 rounded-lg flex flex-col items-center justify-center transition-all ${
                  isActive
                    ? "ring-1 ring-white/30 bg-white/12"
                    : isPast
                    ? "bg-white/[0.08] opacity-90"
                    : "bg-white/[0.04] opacity-55 hover:opacity-80"
                }`}
                style={isActive ? { boxShadow: `inset 0 0 0 1px ${step.color}` } : undefined}
              >
                <span
                  className="text-[9px] font-bold leading-none"
                  style={{ color: isActive || isPast ? step.color : undefined }}
                >
                  {isPast ? "✓" : step.short}
                </span>
              </button>
            );
          })}
        </DockGroup>

        <DockDivider />
        </>
        )}

        {/* Ruta */}
        {sandbox ? (
          <div className="flex flex-col gap-1 shrink-0">
            <span className="text-[9px] font-sans uppercase tracking-widest text-white/40">Ruta</span>
            <div className="flex gap-3">
              {ROUTES.map((r) => (
                <button
                  key={r.mode}
                  type="button"
                  disabled={routeLocked}
                  onClick={() => !routeLocked && onRouteMode(r.mode)}
                  title={r.title}
                  className={`text-xs font-sans transition-colors ${
                    routeLocked ? "opacity-30" : routeMode === r.mode ? "text-white" : "text-white/40 hover:text-white/70"
                  }`}
                >
                  {routeMode === r.mode ? `[${r.title}]` : r.title}
                </button>
              ))}
            </div>
          </div>
        ) : (
        <DockGroup label="Ruta" hint="Cómo recorrer las notas de la escala">
          {ROUTES.map((r) => (
            <IconChip
              key={r.mode}
              active={routeMode === r.mode}
              disabled={routeLocked}
              onClick={() => !routeLocked && onRouteMode(r.mode)}
              title={r.title}
            >
              {r.label}
            </IconChip>
          ))}
        </DockGroup>
        )}

        <DockDivider />

        {/* Octavas */}
        {sandbox ? (
          <div className="flex flex-col gap-1 shrink-0">
            <span className="text-[9px] font-sans uppercase tracking-widest text-white/40">Octavas</span>
            <div className="flex gap-3 text-sm font-sans">
              {[1, 2, 3].map((o) => (
                <button
                  key={o}
                  type="button"
                  onClick={() => onOctaves(o)}
                  className={octaves === o ? "text-emerald-300" : "text-white/40 hover:text-white/70"}
                >
                  {octaves === o ? `[${o}]` : o}
                </button>
              ))}
            </div>
          </div>
        ) : (
        <DockGroup label="Octavas" hint="Cantidad de octavas a recorrer">
          {[1, 2, 3].map((o) => (
            <IconChip
              key={o}
              active={octaves === o}
              onClick={() => onOctaves(o)}
              title={`${o} octava(s)`}
              className="min-w-[1.6rem] font-bold"
            >
              {o}
            </IconChip>
          ))}
        </DockGroup>
        )}

        <DockDivider />

        {/* Dirección */}
        {sandbox ? (
          <div className="flex flex-col gap-1 shrink-0">
            <span className="text-[9px] font-sans uppercase tracking-widest text-white/40">Dir.</span>
            <div className="flex gap-3 text-sm font-sans">
              {(
                [
                  { d: "asc" as const, label: "↑" },
                  { d: "desc" as const, label: "↓" },
                  { d: "both" as const, label: "↕" },
                ] as const
              ).map(({ d, label }) => (
                <button
                  key={d}
                  type="button"
                  onClick={() => onDirection(d)}
                  className={direction === d ? "text-purple-300" : "text-white/40 hover:text-white/70"}
                >
                  {direction === d ? `[${label}]` : label}
                </button>
              ))}
            </div>
          </div>
        ) : (
        <DockGroup label="Dirección" hint="Sentido del recorrido">
          <IconChip
            active={direction === "asc"}
            onClick={() => onDirection("asc")}
            title="Ascendente"
          >
            ↑
          </IconChip>
          <IconChip
            active={direction === "desc"}
            onClick={() => onDirection("desc")}
            title="Descendente"
          >
            ↓
          </IconChip>
          <IconChip
            active={direction === "both"}
            onClick={() => onDirection("both")}
            title="Ida y vuelta"
          >
            ↕
          </IconChip>
        </DockGroup>
        )}

        <DockDivider />

        {sandbox ? (
          <div className="flex flex-col gap-1 shrink-0 min-w-[8rem]">
            <div className="flex justify-between text-[9px] font-sans uppercase tracking-widest text-white/40">
              <span>Tempo</span>
              <span className="tabular-nums">{bpm}</span>
            </div>
            <input
              type="range"
              min={40}
              max={200}
              value={bpm}
              onChange={(e) => onBpm(Number(e.target.value))}
              className="explore-tempo-slider w-full appearance-none bg-transparent cursor-pointer"
              style={{ height: "1px" }}
            />
          </div>
        ) : (
        <>
        <DockGroup label="Tempo" hint="Velocidad de la reproducción">
          <CircularBpmKnob value={bpm} onChange={onBpm} size={42} className="shrink-0" />
        </DockGroup>

        <DockGroup label="Asignar" hint="Crear tarea con esta configuración">
          <TaskCreatorButton
            type="scale"
            variant="compact"
            title={`Escala ${rootNote} ${scaleLabel}`}
            description={`${pattern}, ${octaves} oct., ${direction}`}
            config={serializeScaleState(rootNote, scaleType, octaves, direction, pattern)}
          />
        </DockGroup>
        </>
        )}

        {sandbox && (
          <TaskCreatorButton
            type="scale"
            variant="link"
            title={`Escala ${rootNote} ${scaleLabel}`}
            description={`${pattern}, ${octaves} oct., ${direction}`}
            config={serializeScaleState(rootNote, scaleType, octaves, direction, pattern)}
          />
        )}

        <div className="flex-1 min-w-[4px]" />

        {/* Práctica: stats en línea */}
        {mode === "practice" && (
          <div className="hidden sm:flex items-center gap-2 text-[10px] font-sans text-slate-400 shrink-0 tabular-nums">
            <span>{timeStr}</span>
            <span className="text-white/20">·</span>
            <span>{precision}%</span>
            {sequenceLength > 0 && (
              <>
                <span className="text-white/20">·</span>
                <span className="text-[#5BA8A8]">
                  {Math.min(currentSeqIndex + 1, sequenceLength)}/{sequenceLength}
                </span>
              </>
            )}
            {isPlaying && (
              <span className="text-green-400">{isListening ? "🎤" : "Toca"}</span>
            )}
          </div>
        )}

        {/* CTA principal — misiones guiadas */}
        {!sandbox && (mode === "learn" ? (
          <button
            type="button"
            onClick={onLearnPrimary}
            disabled={learnBusy}
            className="h-9 px-4 rounded-full bg-[var(--bordona-accent)] text-white text-xs font-sans font-semibold flex items-center gap-1.5 shadow-md hover:bg-[var(--bordona-accent-hover)] shrink-0 disabled:opacity-60"
          >
            <Play className="w-3.5 h-3.5 fill-current" />
            <span className="max-w-[8rem] truncate">{learnCtaLabel}</span>
          </button>
        ) : isPlaying ? (
          <button
            type="button"
            onClick={onStopPractice}
            className="h-9 px-3 rounded-full border border-white/20 text-slate-200 text-xs font-sans flex items-center gap-1.5 hover:bg-white/10 shrink-0"
          >
            <Square className="w-3.5 h-3.5" />
            Detener
          </button>
        ) : (
          <button
            type="button"
            onClick={onStartPractice}
            className="h-9 px-4 rounded-full bg-[#5BA8A8] text-white text-xs font-sans font-semibold flex items-center gap-1.5 shadow-md hover:bg-[#4A9898] shrink-0"
          >
            <Play className="w-3.5 h-3.5 fill-current" />
            Practicar
          </button>
        ))}
      </div>

      {/* Barra de progreso (solo práctica activa) */}
      {mode === "practice" && sequenceLength > 0 && (
        <div className="flex gap-0.5 px-3 pb-2 -mt-0.5">
          {Array.from({ length: sequenceLength }).map((_, idx) => (
            <div
              key={idx}
              className={`flex-1 h-1 rounded-full transition-colors ${
                idx < currentSeqIndex
                  ? "bg-[#8FB069]"
                  : idx === currentSeqIndex
                  ? "bg-[#5BA8A8]"
                  : "bg-white/10"
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
};
